import { useState, useEffect } from 'react';
import {
  createTypesenseClient,
  getCollectionNames,
  getTypesenseSearchConfigError,
  TYPESENSE_SEARCH_CONFIG_ERROR,
} from '../utils/typesense';

/**
 * National brewery search for the brewery portal autocomplete (no state filter).
 * @param {string} debouncedSearchQuery
 * @param {{ minChars?: number, perPage?: number }} options
 */
export function useBreweryPortalSearch(debouncedSearchQuery, options = {}) {
  const minChars = typeof options.minChars === 'number' ? options.minChars : 2;
  const perPage = typeof options.perPage === 'number' ? options.perPage : 30;

  const [breweries, setBreweries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const trimmedQuery = typeof debouncedSearchQuery === 'string'
      ? debouncedSearchQuery.trim()
      : '';
    if (!trimmedQuery || trimmedQuery.length < minChars) {
      setBreweries([]);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    async function fetchBreweriesNational() {
      try {
        setError(null);
        const configError = getTypesenseSearchConfigError();
        if (configError) {
          setBreweries([]);
          setError(configError);
          setIsLoading(false);
          return;
        }

        const mode = (import.meta.env.MODE || 'development').toLowerCase();
        const env = ['development', 'staging', 'production'].includes(mode)
          ? mode
          : 'development';
        const { breweries: collectionName } = getCollectionNames(env);

        if (!collectionName) {
          setBreweries([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setIsLoading(false);
          return;
        }

        const client = createTypesenseClient();
        if (!client) {
          setBreweries([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setIsLoading(false);
          return;
        }

        const searchResponse = await client
          .collections(collectionName)
          .documents()
          .search({
            q: trimmedQuery,
            query_by: 'name',
            sort_by: 'name:asc',
            per_page: perPage,
            page: 1,
          });

        if (cancelled) return;

        const hits = searchResponse?.hits ?? [];
        setBreweries(
          hits.map((hitEntry) => {
            const rawDoc = hitEntry.document ?? hitEntry;
            return {
              id: rawDoc.id ?? hitEntry.id,
              data: rawDoc,
            };
          }),
        );
      } catch (fetchError) {
        if (!cancelled) {
          console.error('National brewery Typesense search failed:', fetchError);
          setBreweries([]);
          setError(
            fetchError?.message || 'Failed to load breweries. Please try again.',
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchBreweriesNational();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchQuery, minChars, perPage]);

  return {
    breweries,
    isLoading,
    error,
  };
}
